import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { useGetExerciseOptions } from '../../../../redux/api/exerciseConfigAPI';
import { usePutTrainingSetItem } from '../../../../redux/api/trainingsAPI';
import { SetType, TrainingSetItemStatus } from '../../../../shared/constants';
import { ITrainingSetItem } from '../../../../types/interfaces/training-set-item';
import { ITrainingSetItemOption } from '../../../../types/interfaces/training-set-item-option';
import { getTrainingSetItemFormValidation } from './FormikData';
import { useCurrentTrainingDebounce } from './useCurrentTrainingDebounce';

export function useCurrentTrainingSetItemComponent(
  trainingSetItem: ITrainingSetItem,
  trainingSetId: string,
  trainingId: string,
  onActivateTimer: (value: number) => void,
) {
  const { data: exerciseOptions } = useGetExerciseOptions();
  const [updateTrainingSetItem] = usePutTrainingSetItem();
  const { t } = useTranslation();
  const [prevTrainingSetItemState] =
    useState<ITrainingSetItem>(trainingSetItem);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: getTrainingSetItemFormValidation(t),
    initialValues: {
      rest: trainingSetItem.rest || 0,
      status: trainingSetItem.status || TrainingSetItemStatus.notStarted,
      type: trainingSetItem.type || SetType.normal,
      trainingSetItemOptions: trainingSetItem.trainingSetItemOptions || [],
    },
    onSubmit,
  });

  useEffect(() => {
    if (
      prevTrainingSetItemState &&
      prevTrainingSetItemState.status !== formik.values?.status &&
      formik.values?.status === TrainingSetItemStatus.finished
    ) {
      onActivateTimer(trainingSetItem.rest);
    }
  }, [formik.values?.status]);

  useCurrentTrainingDebounce(formik);

  function onSubmit(values: {
    trainingSetItemOptions: ITrainingSetItemOption[];
    status: TrainingSetItemStatus;
    type: SetType;
  }) {
    const useDefaultValues =
      trainingSetItem.status !== values.status &&
      values.status === TrainingSetItemStatus.finished;

    const skipInvalidation =
      trainingSetItem.status === values.status &&
      trainingSetItem.type === values.type;

    const mapOptionValue = (
      value: unknown,
      programValue: number | null | undefined,
    ): number | null => {
      if (value === null || value === undefined) {
        return useDefaultValues ? (programValue ?? null) : null;
      }

      if (typeof value === 'string') {
        if (value.trim() === '') {
          return useDefaultValues ? (programValue ?? null) : null;
        }

        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? null : parsedValue;
      }

      if (typeof value !== 'number') {
        return null;
      }

      return value;
    };

    const updatedSetItem = {
      ...trainingSetItem,
      status: values.status,
      type: values.type,
      trainingSetItemOptions: values.trainingSetItemOptions.map((option) => ({
        ...option,
        value: mapOptionValue(option.value, option.programValue),
        value2: mapOptionValue(option.value2, option.programValue2),
      })),
    };

    // for cases when we set default values
    formik.setValues({ ...formik.values, ...updatedSetItem });

    updateTrainingSetItem({
      trainingId: trainingId,
      trainingSetId: trainingSetId,
      trainingSetItemId: trainingSetItem.id,
      skipInvalidation,
      body: updatedSetItem,
    })
      .unwrap()
      .catch((e) => {
        console.log(e);
      });
  }

  const onChangeTrainingSetItemOrder = (
    updatedTrainingSetItem: ITrainingSetItem,
  ) => {
    updateTrainingSetItem({
      trainingId: trainingId,
      trainingSetId: trainingSetId,
      trainingSetItemId: updatedTrainingSetItem.id,
      body: {
        ...updatedTrainingSetItem,
        orderNumber: trainingSetItem.orderNumber,
      },
    });
  };

  return {
    formik,
    exerciseOptions,
    onChangeTrainingSetItemOrder,
  };
}
